// =============================================================================
// features/sales/contacts/BlockContactDialog.tsx — Bloqueio/desbloqueio de
// canal de contato ("não contatar", doc 26 §6.3 "Formulário de bloqueio/
// desbloqueio... para quem tem permissão de escrita, com confirmação de
// impacto").
//
// `contact_preferences` é log de auditoria append-only (doc 26 §8) — este
// diálogo só cria uma linha NOVA por canal (nunca edita a anterior). Um
// bloqueio ativo sempre vence campanha, fila, agenda automática e discador
// — por isso a confirmação de impacto é obrigatória (checkbox) antes de
// habilitar o envio, não apenas um aviso passivo.
// =============================================================================

import type {
  ContactChannel,
  ContactPreferenceChannelState,
  ContactPreferenceState,
} from '@elemento/shared-schemas';
import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useSetContactPreference } from '../../../hooks/sales/useContactPreferences';
import { cn } from '../../../lib/cn';
import { orgLocalToUtcIso } from '../components/dateTimeOrgTz';
import { FormTextarea } from '../components/FormTextarea';
import { useFocusTrap } from '../components/useFocusTrap';

import { CHANNEL_LABELS } from './labels';

// ---------------------------------------------------------------------------
// Schema — motivo obrigatório ao BLOQUEAR (ao desbloquear é livre); a
// confirmação de impacto (`confirmImpact`) é obrigatória nos dois sentidos —
// desbloquear também tem impacto (reabre o canal para campanha/fila/discador).
// ---------------------------------------------------------------------------

export const BlockContactSchema = z
  .object({
    channel: z.string().min(1, 'Selecione o canal'),
    state: z.enum(['blocked', 'allowed'], { errorMap: () => ({ message: 'Selecione a ação' }) }),
    reason: z.string().max(500, 'Máximo 500 caracteres').optional(),
    expiresAt: z.string().optional(),
    confirmImpact: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.state === 'blocked' && (!data.reason || data.reason.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: 'Informe o motivo do bloqueio — fica registrado na auditoria.',
      });
    }
    if (!data.confirmImpact) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmImpact'],
        message: 'Confirme que entende o impacto antes de continuar.',
      });
    }
  });

export type BlockContactFormValues = z.infer<typeof BlockContactSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BlockContactDialogProps {
  leadId: string;
  leadName?: string;
  /** Estado atual por canal (GET /api/sales/leads/:leadId/preferences) — só leitura, para o resumo. */
  preferences: ContactPreferenceChannelState[];
  onClose: () => void;
  onChanged?: () => void;
}

const ACTION_OPTIONS: { value: ContactPreferenceState; label: string }[] = [
  { value: 'blocked', label: 'Bloquear contato' },
  { value: 'allowed', label: 'Permitir contato' },
];

export function BlockContactDialog({
  leadId,
  leadName,
  preferences,
  onClose,
  onChanged,
}: BlockContactDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const channelOptions = CONTACT_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }));

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<BlockContactFormValues>({
    resolver: zodResolver(BlockContactSchema),
    defaultValues: {
      channel: CONTACT_CHANNELS[0],
      state: 'blocked',
      reason: '',
      expiresAt: '',
      confirmImpact: false,
    },
  });

  const state = watch('state');
  const { setAsync } = useSetContactPreference(leadId);

  const handleEscape = React.useCallback(() => {
    if (isDirty && !isSubmitting) return; // guarda de edição
    if (!isSubmitting) onClose();
  }, [isDirty, isSubmitting, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function onSubmit(values: BlockContactFormValues): Promise<void> {
    setSubmitError(null);
    try {
      const expiresAtIso = values.expiresAt ? orgLocalToUtcIso(values.expiresAt, '23:59') : null;

      await setAsync({
        // Só pode ser um CONTACT_CHANNELS válido — `channelOptions` acima.
        channel: values.channel as ContactChannel,
        body: {
          state: values.state,
          ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
          ...(expiresAtIso ? { expiresAt: expiresAtIso } : {}),
        },
      });
      onChanged?.();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível salvar a preferência de contato. Tente novamente.';
      setSubmitError(message);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="block-contact-title"
        className={cn(
          'w-full max-w-lg rounded-md flex flex-col overflow-hidden',
          'max-h-[min(90vh,760px)]',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <h2
              id="block-contact-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Gerenciar contato
            </h2>
            {leadName && <p className="font-sans text-sm text-ink-3">{leadName}</p>}
          </div>

          <button
            type="button"
            onClick={handleEscape}
            disabled={isSubmitting}
            aria-label="Fechar diálogo de gerenciamento de contato"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
          className="flex flex-col gap-5 px-6 py-5 overflow-y-auto"
        >
          {/* Resumo do estado atual — texto, não só cor (mesma regra do banner) */}
          {preferences.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
                Estado atual por canal
              </p>
              <ul className="flex flex-col gap-1.5">
                {preferences.map((pref) => (
                  <li key={pref.channel} className="flex items-center justify-between gap-2">
                    <span className="font-sans text-sm text-ink-2">
                      {CHANNEL_LABELS[pref.channel]}
                    </span>
                    <Badge variant={pref.state === 'blocked' ? 'danger' : 'success'}>
                      {pref.state === 'blocked' ? 'Bloqueado' : 'Permitido'}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Select
              id="block-contact-channel"
              label="Canal"
              required
              options={channelOptions}
              error={errors.channel?.message}
              disabled={isSubmitting}
              {...register('channel')}
            />
            <Select
              id="block-contact-action"
              label="Ação"
              required
              options={ACTION_OPTIONS}
              error={errors.state?.message}
              disabled={isSubmitting}
              {...register('state')}
            />
          </div>

          <FormTextarea
            id="block-contact-reason"
            label="Motivo"
            required={state === 'blocked'}
            placeholder={
              state === 'blocked'
                ? 'Ex: titular solicitou não ser mais contatado'
                : 'Opcional — por que o bloqueio está sendo revertido'
            }
            disabled={isSubmitting}
            error={errors.reason?.message}
            {...register('reason')}
          />

          <Input
            id="block-contact-expires"
            label="Válido até"
            type="date"
            hint="Deixe em branco para sem prazo definido."
            disabled={isSubmitting}
            {...register('expiresAt')}
          />

          {/* Confirmação de impacto — checkbox obrigatório, texto sempre visível */}
          <div
            className="flex flex-col gap-2 rounded-sm px-4 py-3 border-l-[3px]"
            style={{
              borderColor: 'var(--warning)',
              background: 'var(--warning-bg)',
              boxShadow: 'var(--elev-1)',
            }}
          >
            <p className="font-sans text-xs font-semibold text-ink">
              {state === 'blocked'
                ? 'Bloquear interrompe campanha, fila, agenda automática e discador para este canal — imediatamente, inclusive carteiras já distribuídas.'
                : 'Permitir reabre este canal para campanha, fila, agenda automática e discador.'}
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-xs shrink-0',
                  'border border-border-strong',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
                )}
                disabled={isSubmitting}
                {...register('confirmImpact')}
              />
              <span className="font-sans text-xs text-ink-2">Entendo o impacto e confirmo.</span>
            </label>
            {errors.confirmImpact && (
              <span role="alert" className="text-xs text-danger">
                {errors.confirmImpact.message}
              </span>
            )}
          </div>

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Erro ao salvar</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1 shrink-0"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="submit"
              variant={state === 'blocked' ? 'danger' : 'primary'}
              disabled={isSubmitting}
              className="w-full justify-center"
            >
              {isSubmitting
                ? 'Salvando…'
                : submitError
                  ? 'Tentar novamente'
                  : state === 'blocked'
                    ? 'Bloquear contato'
                    : 'Permitir contato'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
